import { CONTENTFUL_COMPONENT } from '../../../shared/constants';
import { ICard, ICardWrapper, ITabWrapper } from '../../../types/shared';

import { IMailTemplate } from '../../../services/re-captcha.service';
import { WizardState } from '../../../state/wizard/Wizard.store.types';
import { WizardItem, WizardResultItem } from '../../../types/wizard';

import { ActiveItem } from './WizardContactForm.helpers';
import {
  GroupedWizardItem,
  WizardBodyItem,
  WizardItemResult,
  WizardResultBodyItemName,
} from './WizardResultLayout.types';

interface ParseEmailTemplateProps {
  toAddress: string;
  subject: string;
  body: string;
}

const parseBody = (
  items: (ICard | ICardWrapper | ITabWrapper)[]
): WizardBodyItem[] =>
  items.map((item) => ({
    ...item,
    name: parseBodyName(item.__typename || ''),
  }));

const parseBodyName = (type: string) => {
  switch (type) {
    case CONTENTFUL_COMPONENT.tabWrapper:
      return WizardResultBodyItemName.TAB_WRAPPER;
    case CONTENTFUL_COMPONENT.cardWrapper:
      return WizardResultBodyItemName.CARD_WRAPPER;
    case CONTENTFUL_COMPONENT.card:
    default:
      return WizardResultBodyItemName.CARD;
  }
};

const parseGroupedResults = (results: WizardItem[]): GroupedWizardItem[] =>
  results.reduce(
    (
      accumulator: GroupedWizardItem[],
      result: WizardItem
    ): GroupedWizardItem[] => {
      const foundGroup = accumulator.find(
        (group) => group.id === result.parentId
      );

      if (foundGroup) {
        foundGroup.items.push(result);
        return accumulator;
      }

      return [
        ...accumulator,
        {
          id: result.parentId || '',
          title: result.title || '',
          items: [result],
        },
      ];
    },
    []
  );

const parseResultsToText = (results: WizardItemResult[]) =>
  results.reduce((accumulator, item: WizardItemResult) => {
    return `${accumulator}${item.title}:\n${item.results
      .map((result) => `- ${result.title}\n`)
      .join('')}\n\n`;
  }, '');

const parseActiveItemsToText = (items: ActiveItem[]) =>
  items.reduce(
    (accumulator, item) => `${accumulator}- ${item.title}: ${item.answers}\n`,
    ''
  );

const parseEmailBody = (
  results: WizardResultItem[],
  getItem: WizardState['getItem']
): string => {
  const parentItemIDs = results.map((result) => result.parentId || '');
  const emailBody = parentItemIDs.reduce((accumulator, id, index) => {
    const parentItem = getItem(id);
    const resultItems = results.filter((result) => result.parentId === id);
    const resultList = `${resultItems.map((result, resultIndex) => {
      const seperatorResult = resultIndex > 0 ? '\n' : '\n\n';

      return `${seperatorResult}- ${result.title} ${result.textValue}`;
    })}`;
    const seperatorParent = index > 0 ? '\n\n' : '';

    return `${accumulator}${seperatorParent}${parentItem.title}${resultList}`;
  }, '');

  return emailBody;
};

const parseEmailTemplate = ({
  toAddress,
  subject,
  body,
}: ParseEmailTemplateProps): IMailTemplate => ({
  message: {
    toRecipients: [
      {
        emailAddress: {
          address: toAddress,
        },
      },
    ],
    subject,
    body: {
      contentType: 'text',
      content: body,
    },
  },
});

export {
  parseActiveItemsToText,
  parseBody,
  parseEmailBody,
  parseEmailTemplate,
  parseGroupedResults,
  parseResultsToText,
};
